import React, { useState, useEffect } from 'react'
import { Select, TextInput, Button, Flex, Textarea, Stack, Text, Card, Group } from '@mantine/core'
import actionList from './actionList'
import { getUuid } from "@util/getUuid";

export default function EntityActionAssociatorForm({ onItemSubmit, providerInfo, actionId }) {
  const [formError, setFormError] = useState(null);
  const [formValues, setFormValues] = useState({
    action_id: "",
    provider: "",
    snippet: "",
    description: "",
  });

  useEffect(() => {
    if (actionId) {
      setFormValues({ ...formValues, action_id: actionId });
    }
  }, [actionId]);

  function onSubmit() {
    setFormError(null);

    const data = extractSnippetData(formValues.snippet);
    if (!data.event_category || !data.event_action) {
      setFormError("Invalid snippet");
      return;
    }

    const action = {
      ...data,
      action_id: formValues.action_id,
      snippet: formValues.snippet,
      created_at: new Date().toISOString(),
      id: getUuid(),
      description: formValues.description,
      provider: data.provider === "gtag" ? "gtm" : "meta_pixel",
    };

    if (providerInfo) {
      if (action.provider !== providerInfo.provider) {
        return setFormError(`Expected provider is ${providerInfo.provider_name}. Please check your snippet.`);
      }
    }

    setFormValues({
      ...formValues,
      description: "",
      snippet: "",
    });

    onItemSubmit(action)
  }

  const submitDisabled = !formValues.action_id || !formValues.snippet;

  return (
    <Stack gap="xs">
      {!actionId && <Select
        label="Action"
        value={formValues.action_id}
        onChange={(value) => setFormValues({ ...formValues, action_id: value })}
        data={actionList.map(m => ({ label: m.name, value: `${m.id}`, description: m.description }))}
        placeholder="Select an action"
        required
        searchable
        renderOption={renderSelectOption}
      />}
      <Textarea
        label="Snippet"
        value={formValues.snippet}
        onChange={(e) => setFormValues({ ...formValues, snippet: e.target.value })}
        placeholder="Enter the snippet provided by the provider"
        autosize
        minRows={4}
        required
      />
      {/* <Text size="xs" c="dimmed">Populate with sample snippets</Text>
      <Flex gap="xs" wrap="wrap">
        {sampleSnippets.map((m, idx) => <Button key={idx} size="xs" radius="lg" variant="light" onClick={() => setFormValues({ ...formValues, snippet: m.snippet })}>{m.provider}</Button>)}
      </Flex> */}
      <Textarea
        label="Description (optional)"
        value={formValues.description}
        onChange={(e) => setFormValues({ ...formValues, description: e.target.value })}
        placeholder="Enter a description for your use only"
        autosize
      />
      {formError && <Text size="sm" c="red" fw={600}>{formError}</Text>}
      <Button onClick={onSubmit} disabled={submitDisabled}>Add</Button>
    </Stack>
  )
}

function extractSnippetData(oldSnippet) {
  const snippet = sanitizeConversionSnippet(oldSnippet)
  const re = /\b(gtag|fbq)\(\s*(['"])([^'"]*)\2\s*,\s*(['"])([^'"]*)\4(?:\s*,\s*([\s\S]+?))?\s*\)/;
  const match = re.exec(snippet);
  const rawThird = match?.[6]?.trim();
  const toJson = (str) =>
    str
      .replace(/([\{\,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)(\s*:)/g, '$1"$2"$3') // quote keys for JSON
      .replace(/\\'/g, '\u0000')
      .replace(/'/g, '"')
      .replace(/\u0000/g, "'");
  const value = rawThird ? JSON.parse(toJson(rawThird)) : {};

  return {
    provider: match?.[1],       // "gtag" or "fbq"
    event_category: match?.[3],
    event_action: match?.[5],
    event_value: value,
  };
}

const ALLOWED_CONVERSION_PARAMS = [
  'send_to', 'groups', 'event_timeout', 'allow_custom_scripts',
  'campaign_content', 'campaign_medium', 'campaign_source', 'campaign_term',
  'currency', 'value', 'coupon', 'shipping', 'shipping_tier', 'tax', 'transaction_id',
  'affiliation', 'creative_name', 'creative_slot', 'discount', 'index',
  'item_brand', 'item_category', 'item_category2', 'item_category3', 'item_category4', 'item_category5',
  'item_id', 'item_list_id', 'item_list_name', 'item_name', 'item_variant', 'location_id',
  'price', 'promotion_id', 'promotion_name', 'quantity', 'items',
  'achievement_id', 'character', 'level', 'level_name', 'score', 'virtual_currency_name',
  'file_extension', 'file_name', 'form_destination', 'form_id', 'form_name', 'form_submit_text',
  'group_id', 'language', 'percent_scrolled', 'search_term', 'success', 'visible',
  'link_classes', 'link_domain', 'link_id', 'link_url', 'outbound',
  'content_group', 'content_id', 'content_type', 'page_location', 'page_referrer', 'page_title',
  'screen_resolution', 'system_app', 'system_app_update',
  'ad_format', 'ad_platform', 'ad_source', 'ad_unit_id', 'ad_unit_name', 'exposure_time',
  'engagement_time_msec',
  'video_current_time', 'video_duration', 'video_percent', 'video_provider', 'video_title', 'video_url',
  'app_version', 'cancellation_reason', 'fatal', 'firebase_error', 'firebase_error_value',
  'firebase_screen', 'firebase_screen_class', 'firebase_screen_id', 'free_trial',
  'message_device_time', 'message_id', 'message_name', 'message_time', 'message_type',
  'previous_app_version', 'previous_first_open_count', 'previous_os_version',
  'subscription', 'updated_with_analytics', 'in_app_purchase', 'button_text', 'status',
];

const ALLOWED_KEYS_PATTERN = ALLOWED_CONVERSION_PARAMS.join('|');
const OBJECT_KEY_PREFIX = '(?<=[,{])\\s*';

function sanitizeConversionSnippet(text) {
  let result = text;

  result = result.replace(
    /[ \t]*(?:var|let|const)\s+callback\s*=\s*function\s*\([^)]*\)\s*\{[^}]*\{[^}]*\}[^}]*\}\s*;\s*/g,
    ''
  );

  result = result.replace(
    /(?:^|\n|,)\s*(?:'[^']+'|"[^"]+"|\w+)\s*:\s*(?!true\b|false\b)(?!")(?!')(?!-?\d)\S[^,\n]*\s*,?\n?/g,
    ''
  );

  // Single-quoted keys (inside object only)
  result = result.replace(
    new RegExp(
      OBJECT_KEY_PREFIX + `'(?!${ALLOWED_KEYS_PATTERN}')([^']+)'\\s*:\\s*("[^"]*"|'[^']*'|true|false|-?\\d+\\.?\\d*)\\s*,?\\n?`,
      'g'
    ),
    ''
  );

  // Double-quoted keys (inside object only)
  result = result.replace(
    new RegExp(
      OBJECT_KEY_PREFIX + `"(?!${ALLOWED_KEYS_PATTERN}")([^"]+)"\\s*:\\s*("[^"]*"|'[^']*'|true|false|-?\\d+\\.?\\d*)\\s*,?\\n?`,
      'g'
    ),
    ''
  );

  // Unquoted keys (inside object only)
  result = result.replace(
    new RegExp(
      OBJECT_KEY_PREFIX + `(?!${ALLOWED_KEYS_PATTERN}\\b)(\\w+)\\s*:\\s*("[^"]*"|'[^']*'|true|false|-?\\d+\\.?\\d*)\\s*,?\\n?`,
      'g'
    ),
    ''
  );

  result = result.replace(/,(\s*)}/g, '$1}');
  result = result.replace(/,(\s*),/g, ',');

  return result;
}

const sampleSnippets = [
  {
    provider: "meta_pixel",
    snippet: "fbq('track', 'PageView');",
  },
  {
    provider: "gtm",
    snippet: "gtag('event', 'page_view');",
  },
  {
    provider: "gtm",
    snippet: "gtag('event', 'page_view', { event_category: 'page_view', event_action: 'page_view' });",
  },
  {
    provider: "meta_pixel",
    snippet: "fbq('track', 'PageView', { event_category: 'page_view', event_action: 'page_view' });",
  },
  {
    provider: "gtm",
    snippet: "gtag('event', 'page_view', { send_to: 'AW-1017383762/W_RfCKO5z_sbENKWkOUD' });",
  },
  {
    provider: "meta_pixel",
    snippet: "fbq('trackCustom', 'LRCRegistrations', { button_text: 'save' });",
  }
];

const renderSelectOption = ({ option, checked }) => <Flex gap={1} direction="column">
  <Text size="xs" fw={600}>{option.label}</Text>
  <Text size="xs" c="dimmed">{option.description}</Text>
</Flex>